import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Search,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Truck,
} from "lucide-react";
import { assetUnitService } from "../../api/masterService";
import { reportService } from "../../api/reportService";

const getUnitLabel = (unit) =>
  String(unit?.name || unit?.unit_name || unit?.label || "").trim();
const getUnitType = (unit) => String(unit?.jenis || unit?.type || "-").trim();

const getMonthlyTransactionCount = (unit) => {
  const apiVal =
    unit?.monthly_transaction_count ??
    unit?.transaction_count ??
    unit?.total_transactions ??
    unit?.total_tx ??
    unit?.monthly_transactions ??
    unit?.transactions_this_month ??
    null;
  if (apiVal !== null && apiVal !== undefined) {
    return Number(apiVal) || 0;
  }
  return 0;
};

export default function UnitModal({
  isOpen,
  onClose,
  onSelect,
  currentUnit = null,
}) {
  const [search, setSearch] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortDir, setSortDir] = useState("none"); // 'none' | 'asc' | 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleSort = () => {
    setSortDir((prev) =>
      prev === "none" ? "asc" : prev === "asc" ? "desc" : "none",
    );
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchUnits();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!currentUnit) {
      setSelectedUnit(null);
      return;
    }

    const matchedUnit = units.find((unit) => {
      const currentId = String(currentUnit?.id || "");
      const unitId = String(unit?.id || "");
      if (currentId && unitId && currentId === unitId) return true;

      const currentName = String(
        currentUnit?.name || currentUnit?.unit_name || currentUnit || "",
      )
        .trim()
        .toLowerCase();
      const unitName = getUnitLabel(unit).toLowerCase();
      return currentName && unitName && currentName === unitName;
    });

    setSelectedUnit(matchedUnit || currentUnit);
  }, [isOpen, currentUnit, units]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const [unitRes, recapRes] = await Promise.allSettled([
        assetUnitService.listAll({ status: "ACTIVE" }),
        reportService.getUnitRecap({
          start_date: "2020-01-01",
          end_date: "2099-12-31",
          limit: 1000,
        }),
      ]);

      const rawUnitVal = unitRes.status === "fulfilled" ? unitRes.value : null;
      const rawUnits = Array.isArray(rawUnitVal)
        ? rawUnitVal
        : Array.isArray(rawUnitVal?.data)
        ? rawUnitVal.data
        : Array.isArray(rawUnitVal?.data?.items)
        ? rawUnitVal.data.items
        : Array.isArray(rawUnitVal?.items)
        ? rawUnitVal.items
        : [];

      const rawRecapVal = recapRes.status === "fulfilled" ? recapRes.value : null;
      const recapItems = Array.isArray(rawRecapVal)
        ? rawRecapVal
        : Array.isArray(rawRecapVal?.data?.items)
        ? rawRecapVal.data.items
        : Array.isArray(rawRecapVal?.data)
        ? rawRecapVal.data
        : Array.isArray(rawRecapVal?.items)
        ? rawRecapVal.items
        : Array.isArray(rawRecapVal?.data?.data?.items)
        ? rawRecapVal.data.data.items
        : [];

      const measurementUnits = new Set([
        "pcs", "liter", "box", "paket", "gram", "kg", "cup", "botol", "gelas", "porsi", "bungkus", "dus", "lusin", "meter", "karung", "rol", "set"
      ]);

      const validUnits = rawUnits.filter((u) => {
        const name = String(u.name || "").trim().toLowerCase();
        return name && !measurementUnits.has(name);
      });

      const enriched = validUnits.map((u) => {
        const uName = String(u.name || "").trim().toLowerCase();
        const uId = String(u.id || "").trim().toLowerCase();
        const matchingRecap = recapItems.filter((r) => {
          const rUnit = String(r.unit || r.unit_name || "").trim().toLowerCase();
          const rUnitId = String(r.unit_id || "").trim().toLowerCase();
          return (rUnit && rUnit === uName) || (rUnitId && rUnitId === uId);
        });
        const txCount = matchingRecap.length;
        return {
          ...u,
          transaction_count: txCount,
          monthly_transaction_count: txCount,
        };
      });

      setUnits(enriched);
    } catch (error) {
      console.error("Error fetching units:", error);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return units;
    return units.filter(
      (u) =>
        getUnitLabel(u).toLowerCase().includes(q) ||
        getUnitType(u).toLowerCase().includes(q),
    );
  }, [units, search]);

  const sorted = useMemo(() => {
    if (sortDir === "none") return filtered;
    return [...filtered].sort((a, b) => {
      const cmp = getUnitLabel(a).localeCompare(getUnitLabel(b), "id", {
        sensitivity: "base",
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const paginatedUnits = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sorted.slice(start, start + itemsPerPage);
  }, [sorted, currentPage, itemsPerPage]);

  const startEntry = sorted.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, sorted.length);

  const isUnitSelected = (unit) => {
    const selectedId = String(selectedUnit?.id || "");
    const unitId = String(unit?.id || "");
    if (selectedId && unitId && selectedId === unitId) return true;

    const selectedName = getUnitLabel(selectedUnit).toLowerCase();
    const unitName = getUnitLabel(unit).toLowerCase();
    return selectedName && unitName && selectedName === unitName;
  };

  const handleSave = () => {
    if (selectedUnit) {
      onSelect(selectedUnit);
      setSelectedUnit(null);
      setSearch("");
      setCurrentPage(1);
    }
  };

  const handleClear = () => {
    onSelect(null);
    setSelectedUnit(null);
    setSearch("");
    setCurrentPage(1);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Truck className="w-5 h-5 text-teal-600" />
                Pilih Unit
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Silahkan pilih unit alat berat yang akan menggunakan suku cadang
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              title="Tutup">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cari data unit disini..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto min-h-[300px]">
          {loading ? (
            <div className="p-12 text-center text-gray-500 flex flex-col items-center justify-center gap-2">
              <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <span>Memuat seluruh data unit...</span>
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-12 text-center text-gray-400 flex flex-col items-center justify-center">
              <Truck className="w-12 h-12 text-gray-300 mb-2" />
              <p className="text-sm font-semibold text-gray-600">Tidak ada data unit</p>
              <p className="text-xs text-gray-400 mt-1">
                Pastikan data unit alat berat telah ditambahkan pada Master Data
              </p>
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-xs shadow-xs">
                <tr>
                  <th className="text-left py-3 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                    <button
                      type="button"
                      onClick={toggleSort}
                      className="inline-flex items-center gap-1.5 hover:text-gray-900 transition-colors cursor-pointer font-bold">
                      Nama Unit
                      {sortDir === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5 text-teal-600" />
                      ) : sortDir === "desc" ? (
                        <ArrowDown className="w-3.5 h-3.5 text-teal-600" />
                      ) : (
                        <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                    Jenis
                  </th>
                  <th className="text-right py-3 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider border-b whitespace-nowrap">
                    Transaksi
                  </th>
                  <th className="text-center py-3 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider border-b w-24">
                    Pilih
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedUnits.map((unit) => {
                  const tx = getMonthlyTransactionCount(unit);
                  const selected = isUnitSelected(unit);
                  return (
                    <tr
                      key={unit.id}
                      onClick={() => setSelectedUnit(unit)}
                      className={`hover:bg-teal-50/50 cursor-pointer transition-colors ${
                        selected ? "bg-teal-50/70" : ""
                      }`}>
                      <td className="py-3 px-6">
                        <div className="font-semibold text-gray-900">
                          {getUnitLabel(unit)}
                        </div>
                      </td>
                      <td className="py-3 px-6 text-gray-600 text-xs">
                        <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-medium">
                          {getUnitType(unit)}
                        </span>
                      </td>
                      <td
                        className="py-3 px-6 text-right text-sm text-gray-700 font-semibold"
                        title="Jumlah transaksi unit">
                        {tx}
                      </td>
                      <td className="py-3 px-6 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer">
                          <input
                            type="radio"
                            name="unit-selection"
                            checked={selected}
                            onChange={() => setSelectedUnit(unit)}
                            className="sr-only"
                          />
                          <span
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                              selected
                                ? "border-teal-600 bg-teal-600"
                                : "border-gray-300 bg-white"
                            }`}>
                            {selected && (
                              <span className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination & Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-500 font-medium">
            {sorted.length > 0 ? (
              <span>
                Menampilkan {startEntry} - {endEntry} dari Total {sorted.length} Unit
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <div className="flex items-center gap-1 mr-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage <= 1 || loading}
                  className="w-7 h-7 rounded-lg bg-white ring-1 ring-gray-200 text-xs disabled:opacity-40 hover:bg-gray-100 inline-flex items-center justify-center transition cursor-pointer"
                  title="Halaman pertama">
                  <ChevronsLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1 || loading}
                  className="w-7 h-7 rounded-lg bg-white ring-1 ring-gray-200 text-xs disabled:opacity-40 hover:bg-gray-100 inline-flex items-center justify-center transition cursor-pointer"
                  title="Halaman sebelumnya">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="px-2.5 py-0.5 rounded-lg bg-teal-600 text-white text-xs font-bold min-w-7 text-center">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages || loading}
                  className="w-7 h-7 rounded-lg bg-white ring-1 ring-gray-200 text-xs disabled:opacity-40 hover:bg-gray-100 inline-flex items-center justify-center transition cursor-pointer"
                  title="Halaman berikutnya">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  className="w-7 h-7 rounded-lg bg-white ring-1 ring-gray-200 text-xs disabled:opacity-40 hover:bg-gray-100 inline-flex items-center justify-center transition cursor-pointer"
                  title="Halaman terakhir">
                  <ChevronsRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {currentUnit && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl text-xs font-semibold transition-colors cursor-pointer">
                Hapus
              </button>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedUnit}
              className="px-6 py-2 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm cursor-pointer">
              Tambah
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
